/**
 * 折扣計算共用邏輯：POS 購物車、訂單編輯彈窗、結帳彈窗都走這裡，避免三處各自實作。
 *
 * 這支刻意寫成 CommonJS，讓 tests/unit/utils/discount.test.js 可以直接用 node 執行，
 * 不必為單元測試另外拉建置流程；.vue 端一律以 import 取用。
 */

// 金額放大倍率（元 → 分），用來把小數吃掉後改走整數運算
const MONEY_SCALE = 100
// 百分比基數
const PERCENT_SCALE = 100

// 訂單來源 → 該來源的自動折扣啟用旗標。
// 未列出的來源（例如 UBER_EATS 等三方外送訂單）不套用自動折扣，這是預期行為。
const ORDER_SOURCE_DISCOUNT_FLAGS = {
	BACK: 'forPosOrder',
	MOBILE: 'forMobileOrder',
	FRONT: 'forFrontOrder',
}

/**
 * 計算折數折扣要扣掉的金額。
 *
 * 全程整數運算：金額先放大到最小單位、折數用整數百分比，最後才除回來。
 * 直接寫 base - Math.ceil(base * 0.9) 會踩到浮點誤差，例如
 * 100 * 0.55 得到 55.00000000000001，折扣就少 1 元。
 *
 * @param {number} baseAmount 折扣基準金額（元），例如行小計扣掉自動折扣後的餘額
 * @param {number} paidPercent 「應付」百分比而非折扣率，9 折要傳 90
 * @returns {number} 要扣掉的折扣金額（元，整數）
 */
function calculatePercentageDiscount(baseAmount, paidPercent) {
	const baseInMinorUnits = Math.round(Number(baseAmount) * MONEY_SCALE)
	const normalizedPaidPercent = Math.round(Number(paidPercent))
	const discountPercent = PERCENT_SCALE - normalizedPaidPercent

	return Math.floor(
		(baseInMinorUnits * discountPercent) / (MONEY_SCALE * PERCENT_SCALE),
	)
}

/**
 * 應付比例轉整數百分比，例如 0.9 → 90。
 * 購物車上的折數（discountPerson、automaticDiscountPercentage）都以比例儲存，
 * 進 calculatePercentageDiscount 前統一用這支轉換。
 *
 * @param {number|string} paidRatio 應付比例，例如 0.9 或 '0.90'
 * @returns {number} 整數的應付百分比
 */
function ratioToPaidPercent(paidRatio) {
	return Math.round(Number(paidRatio) * PERCENT_SCALE)
}

/**
 * 取得訂單來源對應的自動折扣旗標，來源不支援自動折扣時回傳 null。
 *
 * @param {string} orderSource 訂單來源
 * @returns {string|null}
 */
function getOrderSourceDiscountFlag(orderSource) {
	return ORDER_SOURCE_DISCOUNT_FLAGS[orderSource] || null
}

/**
 * 篩出該訂單來源適用的「商品自動折扣」。
 *
 * @param {Array} discounts 自動折扣清單
 * @param {string} orderSource 訂單來源
 * @returns {Array} 適用的商品自動折扣，來源不支援時為空陣列
 */
function filterItemAutomaticDiscounts(discounts, orderSource) {
	const sourceFlag = getOrderSourceDiscountFlag(orderSource)
	if (!sourceFlag || !Array.isArray(discounts)) {
		return []
	}

	return discounts.filter(
		discount =>
			discount.discountMethod === 'ITEM_SPECIFIC' && discount[sourceFlag],
	)
}

/**
 * 把商品自動折扣規則寫進購物車項目。
 *
 * 金額折扣與折數折扣互斥：寫入一種就把另一種清成 0，否則 getItemCardInfo
 * 兩個分支都會成立而重複扣款（後台把折扣類型從折數改成金額時就會發生）。
 *
 * @param {Object} item 購物車項目，會被就地修改
 * @param {Object} rule 自動折扣規則
 * @param {*} relationId 折扣與商品的關聯 id（automaticDiscountItemId）
 * @returns {Object} 傳入的 item
 */
function applyAutomaticDiscountRule(item, rule, relationId) {
	if (rule.discountType === 'AMOUNT') {
		item.automaticDiscountPrice = rule.discountAmount
		item.automaticDiscountPercentage = 0
	} else if (rule.discountType === 'PERCENTAGE') {
		item.automaticDiscountPrice = 0
		item.automaticDiscountPercentage = (rule.discountPercentage * 0.01).toFixed(
			2,
		)
	} else {
		// 未知折扣類型（例如 ITEM_SPECIFIC 搭配贈送商品）：視同沒有折扣，
		// 並清掉殘留值，免得沿用上一次套用的金額或折數
		item.automaticDiscountId = null
		item.automaticDiscountItemId = null
		item.automaticDiscountPrice = 0
		item.automaticDiscountPercentage = 0
		return item
	}

	item.automaticDiscountId = rule.id
	item.automaticDiscountItemId = relationId

	return item
}

module.exports = {
	applyAutomaticDiscountRule,
	calculatePercentageDiscount,
	filterItemAutomaticDiscounts,
	getOrderSourceDiscountFlag,
	ratioToPaidPercent,
}
